import type { OfficeHoursConfig } from '@bitio-builder/shared';

/**
 * Check if the current time is within office hours
 */
export function isWithinOfficeHours(config?: OfficeHoursConfig): boolean {
  // If no config, always within office hours (chat always available)
  if (!config) {
    return true;
  }

  const { days, startTime, endTime, timezone } = config;

  // Get current time in the configured timezone
  const now = new Date();
  const formatter = new Intl.DateTimeFormat('en-US', {
    timeZone: timezone,
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
    weekday: 'short',
  });

  const parts = formatter.formatToParts(now);
  const hourPart = parts.find(p => p.type === 'hour');
  const minutePart = parts.find(p => p.type === 'minute');
  const weekdayPart = parts.find(p => p.type === 'weekday');

  if (!hourPart || !minutePart || !weekdayPart) {
    // Fallback: assume within office hours if we can't determine
    return true;
  }

  const currentHour = parseInt(hourPart.value, 10);
  const currentMinute = parseInt(minutePart.value, 10);
  const currentTimeMinutes = currentHour * 60 + currentMinute;

  // Map weekday abbreviation to day number (0 = Sunday)
  const weekdayMap: Record<string, number> = {
    'Sun': 0,
    'Mon': 1,
    'Tue': 2,
    'Wed': 3,
    'Thu': 4,
    'Fri': 5,
    'Sat': 6,
  };
  const currentDay = weekdayMap[weekdayPart.value] ?? -1;

  // Check if current day is a working day
  if (!days.includes(currentDay)) {
    return false;
  }

  // Parse start and end times
  const [startHour, startMinute] = startTime.split(':').map(Number);
  const [endHour, endMinute] = endTime.split(':').map(Number);
  const startMinutes = startHour * 60 + startMinute;
  const endMinutes = endHour * 60 + endMinute;

  // Check if current time is within range
  return currentTimeMinutes >= startMinutes && currentTimeMinutes < endMinutes;
}

/**
 * Default office hours: Monday-Friday, 9:00-17:00 CET
 */
export const DEFAULT_OFFICE_HOURS: OfficeHoursConfig = {
  days: [1, 2, 3, 4, 5], // Monday-Friday
  startTime: '09:00',
  endTime: '17:00',
  timezone: 'Europe/Stockholm',
};
